import { Bell, Settings, ChevronDown, MessageCircle, Moon, Sun, Menu } from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "../contexts/ThemeContext";
import { i18n } from "../../translate/i18n";

interface HeaderProps {
  pageTitle: string;
  onMenuClick?: () => void;
}

export function Header({ pageTitle, onMenuClick }: HeaderProps) {
  const { theme, toggleTheme } = useTheme();

  return (
    <header className="flex h-14 sm:h-16 w-full flex-shrink-0 items-center justify-between bg-gradient-to-r from-[#1E40AF] to-[#2563EB] px-3 sm:px-6 shadow-md">
      {/* Left - Hamburger + Logo */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-2 sm:gap-3"
      >
        {/* Hamburger Menu - Mobile Only */}
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            className="flex lg:hidden h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-white transition-all hover:bg-white/20"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
        <div className="flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-xl bg-white/20">
          <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
        </div>
        <span className="text-base sm:text-lg font-bold text-white hidden sm:inline">UniflowBrasil</span>
      </motion.div>

      {/* Center - Page Title */}
      <motion.h1
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        key={pageTitle}
        className="text-sm sm:text-lg font-semibold text-white"
      >
        {pageTitle}
      </motion.h1>

      {/* Right - Actions */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center gap-2 sm:gap-4"
      >
        {/* Dark Mode Toggle */}
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={toggleTheme}
          className="relative rounded-xl bg-white/10 p-2 sm:p-2.5 text-white transition-all hover:bg-white/20"
          title={theme === 'light' ? i18n.t('v2.header.darkMode') : i18n.t('v2.header.lightMode')}
        >
          <motion.div
            initial={false}
            animate={{ rotate: theme === 'dark' ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {theme === 'light' ? (
              <Moon className="h-4 w-4 sm:h-5 sm:w-5" />
            ) : (
              <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </motion.div>
        </motion.button>

        {/* Notification Bell */}
        <button className="relative rounded-xl bg-white/10 p-2 sm:p-2.5 text-white transition-all hover:bg-white/20">
          <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="absolute -right-1 -top-1 flex h-4 w-4 sm:h-5 sm:w-5 items-center justify-center rounded-full bg-[#EF4444] text-[10px] sm:text-xs font-bold text-white">
            3
          </span>
        </button>

        {/* Settings - hide on mobile */}
        <button className="hidden sm:flex rounded-xl bg-white/10 p-2.5 text-white transition-all hover:bg-white/20">
          <Settings className="h-5 w-5" />
        </button>

        {/* User Avatar */}
        <button className="flex items-center gap-1 sm:gap-2 rounded-xl bg-white/10 p-1 sm:p-1.5 pr-2 sm:pr-3 transition-all hover:bg-white/20">
          <img
            src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
            alt="User"
            className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg object-cover"
          />
          <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
        </button>
      </motion.div>
    </header>
  );
}